import { help } from './en/help'
import { externalAccess } from './en/externalAccess'
import { cashRegisters } from './en/cashRegisters'
import {patientTitleSheet} from './en/patientTitleSheet'
import { clinicalSummary } from './en/clinicalSummary'
import { anamnesis } from './en/anamnesis'
import { legalEntities } from './en/legalEntities'
import { clinical } from './en/clinical'
import { clinicalTranslation } from './en/clinicalTranslation'
import { common } from './en/common'
import { controlCenter } from './en/controlCenter'
import { controlCenterAccess } from './en/controlCenterAccess'
import { controlCenterConnectors } from './en/controlCenterConnectors'
import { controlCenterIntegrations } from './en/controlCenterIntegrations'
import { controlCenterModules } from './en/controlCenterModules'
import { correspondence } from './en/correspondence'
import { dashboard } from './en/dashboard'
import { emr } from './en/emr'
import { externalEntry } from './en/externalEntry'
import { egisz } from './en/egisz'
import { externalModules } from './en/externalModules'
import { finance } from './en/finance'
import { financeSettlement } from './en/financeSettlement'
import { financePrepaid } from './en/financePrepaid'
import { priceCatalog } from './en/priceCatalog'
import { imaging } from './en/imaging'
import { laboratory } from './en/laboratory'
import { laboratoryRecognition } from './en/laboratoryRecognition'
import { laboratoryReview } from './en/laboratoryReview'
import { localizedField } from './en/localizedField'
import { leads } from './en/leads'
import { notifications } from './en/notifications'
import { patients } from './en/patients'
import { persons } from './en/persons'
import { schedule } from './en/schedule'
import { tasks } from './en/tasks'
import { processing } from './en/processing'
import { tracker } from './en/tracker'
import { estimates } from './en/estimates'

/**
 * English is the Terysling default and the fallback for every other locale
 * (DR-0003). This catalogue is the key authority: `Messages` is derived from it,
 * so every other locale is type-checked against these ids and a component that
 * asks for an unknown id fails to compile.
 *
 * Ids are stable and semantic. They are never assembled at runtime from user or
 * API data, which keeps the key set statically enumerable for the completeness
 * test in `catalogue.test.ts`.
 *
 * The messages live in one module per product surface so that Schedule,
 * Patients, EMR and Clinical work can proceed on disjoint files. This file is
 * the boundary that hides that: consumers import `MessageId` and the catalogue
 * from here and never learn how the surfaces are stored, which is what lets the
 * grouping change later without touching a single component.
 *
 * Ownership is by namespace, and it is exact — see `catalogue.modules.test.ts`,
 * which fails if two surfaces claim a key or a module stops being composed:
 *
 *   common     app, language, combobox, login, shell, nav, error, notFound,
 *              appearance, table
 *   schedule   schedule, status
 *   patients   patients, patient, patientCard, patientPicker, patientVip,
 *              patientRegistration, patientMerge, recent, medicalSheet, case,
 *              stay — the registry, the card and the chart sections behind it
 *   patientTitleSheet  patient.identity — the identity page of a chart: a
 *              government document read, compared with the record and applied
 *   clinicalSummary  patient.summary — the chart's AI summary, the source
 *              choices it is built from, its Word download and the clinic
 *              letterhead it is laid out on
 *   anamnesis  anamnesis — the patient's versioned life history and
 *              allergy statement: the chart section, the visit tile and the
 *              allergy mark on the patient plate
 *   persons    person — the Person vocabulary a chart and an account card
 *              share, because underneath they are one `core.person` row
 *   emr        upload, category, file, emrJournal, emrEntry
 *   externalEntry  externalEntry — the external clinical entry page, its
 *              lifecycle, translations and AI preparation from documents
 *   finance    finance — the staff workspace's accounts, receipts, people
 *              and administration, except the settlement keys below
 *   financeSettlement  finance.settlement, finance.refunds — the account's
 *              settlement registries and forms, split out by surface
 *   imaging    imaging
 *   clinical   clinical, record, encounterQuick, referral, diagnosis
 *   clinicalTranslation  clinicalTranslation
 *   laboratory lab
 *   laboratoryRecognition  lab.recognition — recognizing a file: the Files
 *              row, the page choice, cancellation and stopped runs
 *   laboratoryReview  lab.review — the draft's rows under their crops, Needs
 *              attention, automatic codes and a report's provenance
 *   localizedField  localizedField — the field one value in several
 *              languages is edited in, shared by every domain that stores one
 *   tasks      task
 *   processing  processing — the workspace tile, the registry and the page
 *              of a processing, and the names of its kinds, statuses and stages
 *   notifications  notifications — the personal inbox behind the header bell
 *   dashboard  dashboard
 *   controlCenter  controlCenter, organization, profile, profileSettings,
 *                  clinicSettings, terminology, protocols, printForm
 *   controlCenterAccess  users, roles — account administration and the
 *                  role/capability registry
 *   controlCenterConnectors  connectors — the shard's SMTP, Telegram and
 *                  WhatsApp connectors: their redacted status, the manager's
 *                  three acts and every refusal the server answers with
 *   controlCenterIntegrations  telegramLink, whatsappLink, storedSecret, googleDocAI,
 *                  googleSignIn, ai — the external-service sections and the
 *                  credential fragment they share
 *   controlCenterModules  clinicModules — the Connected modules section:
 *                  the registered-module catalogue, the three lifecycle
 *                  states and the reasoned switches that move them
 *   correspondence  correspondence, commPrefs, message — a Patient's chronology
 *                  across every channel with its composer, the chart's own
 *                  register of what was sent, and the Person leaf of verified
 *                  addresses, preferences and hours
 *   legalEntities  legalEntity — the paying and contracting parties
 *   leads      lead, referralPartner — an enquiry before it is a Patient, and
 *              who sent it
 *   tracker    tracker
 *   estimates  estimate, externalCosts — treatment cost estimates and the
 *              external-cost catalogue they quote from
 *   externalModules  externalModule — the external-subsystem surfaces: the
 *              registry's External systems column, the identity card's connection
 *              marks, the generic module section, and each registered
 *              module's own `externalModule.<code>.*` words
 *              the launch being found, the reopen answers and the in-frame
 *              refusal, outside the product proper
 */
export const EN_SURFACES = {
  externalAccess,
  help,
  cashRegisters,
  common,
  schedule,
  patients,
  patientTitleSheet,
  clinicalSummary,
  anamnesis,
  persons,
  emr,
  externalEntry,
  finance,
  financeSettlement,
  financePrepaid,
  priceCatalog,
  imaging,
  clinical,
  clinicalTranslation,
  laboratory,
  laboratoryRecognition,
  laboratoryReview,
  localizedField,
  legalEntities,
  leads,
  tasks,
  processing,
  notifications,
  dashboard,
  controlCenter,
  controlCenterAccess,
  controlCenterConnectors,
  controlCenterIntegrations,
  controlCenterModules,
  correspondence,
  tracker,
  estimates,
  externalModules,
  egisz,
} as const

export const en = {
  ...externalAccess,
  ...help,
  ...common,
  ...schedule,
  ...patients,
  ...patientTitleSheet,
  ...clinicalSummary,
  ...anamnesis,
  ...persons,
  ...emr,
  ...externalEntry,
  ...finance,
  ...cashRegisters,
  ...financeSettlement,
  ...financePrepaid,
  ...priceCatalog,
  ...imaging,
  ...clinical,
  ...clinicalTranslation,
  ...laboratory,
  ...laboratoryRecognition,
  ...laboratoryReview,
  ...localizedField,
  ...legalEntities,
  ...leads,
  ...tasks,
  ...processing,
  ...notifications,
  ...dashboard,
  ...controlCenter,
  ...controlCenterAccess,
  ...controlCenterConnectors,
  ...controlCenterIntegrations,
  ...controlCenterModules,
  ...correspondence,
  ...tracker,
  ...estimates,
  ...externalModules,
  ...egisz,
} as const

/**
 * Derived from the COMPOSED English key set, never widened.
 *
 * `string` here would compile every typo in the product and turn the fallback
 * into a silent blank, so `catalogue.modules.test.ts` holds a deliberate
 * unknown-id line that must keep failing to compile.
 */
export type MessageId = keyof typeof en

export type Messages = Record<MessageId, string>
